import React, { useCallback, useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { useFeatureFlag } from "@jsq/flags-react";
import { Banner, Link } from "@jsq/ds/core";
import { useLocalStorage } from "common/hooks/useLocalStorage";
import { identifyEnvironment } from "@jsq/utils";
import * as styles from "./styles_vanilla.css";
import { useGetUnresolvedIncidentIds } from "./hooks";
import { DISMISSED_INCIDENT_IDS_STORAGE_KEY } from "./constants";

// Status page IDs per environment group (sourced from infra values files)
// local/dev/test/preview: config_dev.py, values-test.yaml, values-preview.yaml
// staging/qa/perf/beta*:  values-staging.yaml, values-qa.yaml, values-perf.yaml
// production:             values-production.yaml
const STATUS_PAGE_IDS = {
  development: "5jsfg3nr4qkc",
  staging: "m9vtsz1vp7fw",
  production: "4c0qybx1tbgr",
} as const;

const getPageId = (): string | undefined => {
  const env = identifyEnvironment();

  switch (env.environmentType) {
    case "development":
    case "preview":
    case "test":
      return STATUS_PAGE_IDS.development;
    case "staging":
    case "qa":
    case "perf":
    case "prodops":
    case "cit":
      return STATUS_PAGE_IDS.staging;
    case "production":
      return STATUS_PAGE_IDS.production;
    default:
      return undefined;
  }
};

type StatusPageIncidentBannerProps = {
  message?: string;
  persistDismissal?: boolean;
  stretched?: boolean;
};

export const StatusPageIncidentBanner = ({
  message = "We are currently investigating a temporary system issue affecting certain customers.",
  persistDismissal = false,
  stretched = false,
}: StatusPageIncidentBannerProps) => {
  const pageId = getPageId();
  const [sessionDismissedIds, setSessionDismissedIds] = useState<string[]>([]);
  const displayStatusPageIncidentBanner = useFeatureFlag("statusPageIncidentBanner", false);

  const {
    value: storedDismissedIds,
    setLocalStorageValue: setStoredDismissedIds,
    removeLocalStorageValue: clearStoredDismissedIds,
  } = useLocalStorage<string[]>(DISMISSED_INCIDENT_IDS_STORAGE_KEY, []);

  const storedIds = useMemo(() => storedDismissedIds ?? [], [storedDismissedIds]);
  const dismissedIds = persistDismissal ? storedIds : sessionDismissedIds;

  const { data: incidentIds, statusPageUrl } = useGetUnresolvedIncidentIds({
    pageId,
    skip: !displayStatusPageIncidentBanner,
  });

  const activeIncidentIds = incidentIds?.filter((id) => !dismissedIds.includes(id));

  // Clear persisted dismissals when all incidents are resolved.
  useEffect(() => {
    if (persistDismissal && incidentIds?.length === 0 && storedIds.length > 0) {
      clearStoredDismissedIds();
    }
  }, [persistDismissal, incidentIds, clearStoredDismissedIds, storedIds.length]);

  const handleDismiss = useCallback(() => {
    if (persistDismissal) {
      const newIds = activeIncidentIds?.filter((id) => !storedIds.includes(id)) ?? [];
      if (newIds.length > 0) {
        setStoredDismissedIds([...storedIds, ...newIds]);
      }
    }
    setSessionDismissedIds((prev) => [...prev, ...(activeIncidentIds ?? [])]);
  }, [persistDismissal, activeIncidentIds, storedIds, setStoredDismissedIds]);

  if (!displayStatusPageIncidentBanner || !activeIncidentIds?.length) {
    return null;
  }

  const bannerClassName = classNames(styles.statusPageIncidentBanner, {
    [styles.statusPageIncidentBannerMaxWidth]: !stretched,
  });

  const statusPageLinkText = statusPageUrl && (
    <>
      Please visit{" "}
      <Link
        href={statusPageUrl}
        hasUnderline
        shouldOpenInNewTab
        align="left"
        isInline
        className={styles.statusPageLink}
      >
        {statusPageUrl.replace(/^https?:\/\//, "").replace(/\/$/, "")}
      </Link>{" "}
      for details and updates while we resolve the issue.
    </>
  );

  return (
    <Banner
      className={bannerClassName}
      variant="warning"
      iconVariant="outline"
      title="Service Interruption"
      description={
        <>
          {message} {statusPageUrl && statusPageLinkText}
        </>
      }
      isDescriptionJsx
      onClear={handleDismiss}
    />
  );
};

export default StatusPageIncidentBanner;
